/**
 * InventoryBulkSelectionBar — the floating selection pill for the /inventory
 * list view.
 *
 * Thin skin over the shared `FloatingSelectionBar` primitive (BLU-680) rather
 * than a fourth hand-rolled `fixed bottom-N left-1/2` wrapper: positioning,
 * the sr-only live region, and the enter/exit motion contract all come from
 * the primitive, exactly as on /products and the production OrdersPage.
 *
 * Carries ONLY selection-scoped affordances — count, cross-page select-all,
 * clear, and the per-SKU stock editor. The existing bulk actions (image /
 * delete / same-quantity add / key items) stay in the toolbar inside
 * `PageActionBar`; duplicating them here would be a body-action duplicate
 * (DESIGN.md §10).
 */
import { useTranslation } from 'react-i18next';
import { PackagePlus, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { FloatingSelectionBar } from '@/components/layout/FloatingSelectionBar';

interface InventoryBulkSelectionBarProps {
  /** Rows currently selected (already narrowed by the active filters). */
  selectedCount: number;
  /** Rows the current filters match, across every page. */
  filteredCount: number;
  /** Selects every filtered row, including rows on other pages. */
  onSelectAllFiltered: () => void;
  onClearSelection: () => void;
  onEditStock: () => void;
  /**
   * Gate the editor at the TRIGGER, never inside the dialog (DESIGN.md §13.9)
   * — e.g. "All Locations" is selected, so there is no location to receive at.
   */
  editStockDisabled?: boolean;
  editStockDisabledReason?: string;
}

export function InventoryBulkSelectionBar({
  selectedCount,
  filteredCount,
  onSelectAllFiltered,
  onClearSelection,
  onEditStock,
  editStockDisabled = false,
  editStockDisabledReason,
}: InventoryBulkSelectionBarProps) {
  const { t } = useTranslation('inventory');

  const canSelectAll = filteredCount > 0 && selectedCount < filteredCount;

  return (
    <FloatingSelectionBar
      visible={selectedCount > 0}
      srStatus={t('bulkSelection.srStatus', { count: selectedCount })}
      ariaLabel={t('bulkSelection.toolbarAria')}
      // Keep clear of the BackToTopPill (fixed bottom-6 right-6): the bar is
      // centred, so they only ever approach each other on very narrow
      // viewports — the max-width stops the pill growing into that corner.
      className="max-w-[calc(100vw-9rem)]"
    >
      <span className="min-w-0 truncate text-sm font-medium tabular-nums whitespace-nowrap">
        {t('bulkSelection.count', { count: selectedCount })}
      </span>
      <div className="mx-1.5 h-5 w-px bg-border" aria-hidden="true" />
      {canSelectAll && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="hidden rounded-full sm:inline-flex"
          onClick={onSelectAllFiltered}
        >
          {t('bulkSelection.selectAllFiltered', { count: filteredCount })}
        </Button>
      )}
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              // h-9 keeps one baseline with the sm-size buttons beside it.
              className="h-9 w-9 rounded-full"
              onClick={onClearSelection}
              aria-label={t('bulkSelection.clear')}
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>{t('bulkSelection.clear')}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
      {/* Primary sits last so it lands in the pill's `pr-1.5` well — the same
          geometry the /products bar uses. */}
      {editStockDisabled ? (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              {/* A disabled button emits no pointer events, so the tooltip
                  needs a focusable wrapper to hang off. The reason is not
                  tooltip-only — the filter bar renders it as an amber banner
                  whenever stock actions are disabled (DESIGN.md §10). */}
              <span
                tabIndex={0}
                className="rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                <Button
                  type="button"
                  size="sm"
                  className="gap-1.5 rounded-full bg-threadheads-purple text-white hover:bg-threadheads-purple/90"
                  disabled
                >
                  <PackagePlus className="h-3.5 w-3.5" aria-hidden="true" />
                  {t('bulkSelection.editStock')}
                </Button>
              </span>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">
              {editStockDisabledReason ?? t('filters.allLocationsWarning')}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : (
        <Button
          type="button"
          size="sm"
          className="gap-1.5 rounded-full bg-threadheads-purple text-white hover:bg-threadheads-purple/90"
          onClick={onEditStock}
        >
          <PackagePlus className="h-3.5 w-3.5" aria-hidden="true" />
          {t('bulkSelection.editStock')}
        </Button>
      )}
    </FloatingSelectionBar>
  );
}
